import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { stateName } from "../lib/states";
import { MapPin, Trash2, Loader2, ExternalLink, RefreshCw, Save, Lock, Sparkles } from "lucide-react";

const PORTAL_KEYS = ["assessor", "recorder", "municipal", "tax", "state_court"];

function CountyCard({ row, onClear, onSaved }) {
  const labels = row.portal_labels || {};
  const [draft, setDraft] = useState(() => {
    const d = {};
    PORTAL_KEYS.forEach((k) => { d[k] = (row.portals?.[k]?.url) || ""; });
    return d;
  });
  const [saving, setSaving] = useState(false);
  const dirty = PORTAL_KEYS.some((k) => (draft[k] || "") !== ((row.portals?.[k]?.url) || ""));

  const save = async () => {
    setSaving(true);
    try {
      const portals = {};
      PORTAL_KEYS.forEach((k) => { portals[k] = { url: (draft[k] || "").trim() }; });
      const { data } = await api.patch(`/admin/county-portals/${encodeURIComponent(row.id)}`, { portals });
      onSaved(row.id, data);
      toast.success(`Locked portals for ${row.county} County`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`cache-row-${row.id}`} className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-600 text-slate-800 flex items-center gap-2">
            {row.county} County, {stateName(row.state)}
            {row.verified && <span data-testid={`locked-badge-${row.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700"><Lock className="h-2.5 w-2.5" /> Locked</span>}
          </div>
          <div className="text-[11px] text-slate-400">Updated {row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}{row.updated_by_name ? ` · ${row.updated_by_name}` : ""}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button data-testid={`save-cache-${row.id}`} onClick={save} disabled={!dirty || saving}
            className="inline-flex items-center gap-1 rounded-md bg-[#1A395E] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#24497A] transition-colors disabled:opacity-40">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Lock links
          </button>
          <button data-testid={`clear-cache-${row.id}`} onClick={() => onClear(row.id)}
            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">
            <Trash2 className="h-3.5 w-3.5" /> Reset to AI
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        {PORTAL_KEYS.map((k) => {
          const ov = row.portals?.[k]?.override;
          return (
            <div key={k} className="flex items-center gap-2">
              <div className="w-40 shrink-0 text-[12px] font-semibold text-slate-600 flex items-center gap-1">
                {ov ? <Lock className="h-3 w-3 text-emerald-600" /> : <Sparkles className="h-3 w-3 text-slate-300" title="AI-sourced" />}
                {labels[k] || k}
              </div>
              <input data-testid={`portal-input-${row.id}-${k}`} value={draft[k]} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                placeholder="https://… (leave blank to remove)"
                className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-[12px] font-mono focus:border-teal focus:outline-none" />
              {draft[k] && <a href={draft[k]} target="_blank" rel="noopener noreferrer" className="shrink-0 text-slate-400 hover:text-teal" title="Open"><ExternalLink className="h-4 w-4" /></a>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CountyPortals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/county-portals"); setRows(data); }
    catch { toast.error("Could not load cached county portals"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const clearOne = async (id) => {
    if (!window.confirm(`Reset ${id} to AI? Your manual overrides for this county will be cleared and re-fetched on its next order.`)) return;
    try { await api.delete(`/admin/county-portals/${encodeURIComponent(id)}`); toast.success("Reset — will re-fetch from AI"); setRows((p) => p.filter((r) => r.id !== id)); }
    catch { toast.error("Could not reset"); }
  };
  const onSaved = (id, data) => setRows((p) => p.map((r) => r.id === id ? { ...r, portals: data.portals, verified: data.verified, updated_at: new Date().toISOString() } : r));

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="county-portals-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-2xl font-700 text-slate-900 flex items-center gap-2"><MapPin className="h-5 w-5 text-teal" /> County Portal Overrides</h1>
        <button data-testid="refresh-cache-list" onClick={load} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Reload
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-5">Fix the Assessor / Recorder / Municipal / Tax / State-Court URLs the AI gets wrong. Edit a URL and click <b>Lock links</b> — locked links are never overwritten by AI refreshes and are shown to every order in that county. <b>Reset to AI</b> clears your overrides.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="cache-empty">No county portals cached yet. They are created the first time an order in a county opens its Quick Research card.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => <CountyCard key={r.id} row={r} onClear={clearOne} onSaved={onSaved} />)}
        </div>
      )}
    </div>
  );
}
